import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Globe, Sparkles, Palette, Brain, Link2 } from 'lucide-react';

const STAGES = [
  { key: 'extracting', label: 'Reviewing content', icon: Globe },
  { key: 'downloading', label: 'Reviewing content', icon: Globe },
  { key: 'transcribing', label: 'Transcribing audio', icon: Globe },
  { key: 'analyzing', label: 'AI analysis', icon: Sparkles },
  { key: 'enriching', label: 'Finishing touches', icon: Palette },
];

// Pull the registrable host out of a URL string so we can show the
// recipient site favicon + domain in the processing placeholder. Falls
// back gracefully for malformed inputs (e.g. raw text saved by share).
function _hostFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h.startsWith('www.') ? h.slice(4) : h;
  } catch (_e) {
    return null;
  }
}

// Map any stage to its display position (some stages share a visual step)
const DISPLAY_STEPS = [
  { label: 'Reviewing', stages: ['extracting', 'downloading', 'transcribing'] },
  { label: 'Analyzing', stages: ['analyzing'] },
  { label: 'Finishing', stages: ['enriching'] },
];

export function ProcessingSteps({ stage }) {
  // Find which display step the current stage belongs to
  const activeDisplayIdx = DISPLAY_STEPS.findIndex(ds => ds.stages.includes(stage));
  // Get the detailed label for the current stage
  const detailedStage = STAGES.find(s => s.key === stage);
  const detailedLabel = detailedStage ? detailedStage.label : 'Processing';

  return (
    <div className="flex items-center gap-1.5" data-testid="processing-steps">
      {DISPLAY_STEPS.map((ds, i) => {
        const isActive = ds.stages.includes(stage);
        const isDone = activeDisplayIdx > i;
        const icons = [Globe, Sparkles, Palette];
        const Icon = icons[i] || Globe;

        return (
          <React.Fragment key={ds.label}>
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all duration-300
                ${isActive ? 'bg-primary/10 text-primary' : ''}
                ${isDone ? 'text-primary/60' : ''}
                ${!isActive && !isDone ? 'text-muted-foreground/40' : ''}
              `}
              data-testid={`processing-step-${ds.label.toLowerCase()}`}
            >
              {isActive ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : isDone ? (
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              ) : (
                <Icon className="w-3 h-3" />
              )}
              <span className="hidden sm:inline">{isActive ? detailedLabel : ds.label}</span>
            </div>
            {i < DISPLAY_STEPS.length - 1 && (
              <div className={`w-4 h-px transition-colors duration-300 ${isDone ? 'bg-primary/40' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function ProcessingCardOverlay({ stage, updatedAt, onRetry, url }) {
  const stageData = STAGES.find(s => s.key === stage) || STAGES[0];
  // Stuck-detection is time-based; setState calls must be deferred to
  // a callback so the react-hooks/set-state-in-effect rule is satisfied.
  const [isStuck, setIsStuck] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const stuck = !!(updatedAt && Date.now() - new Date(updatedAt).getTime() > 3 * 60 * 1000);
      setIsStuck(stuck);
    };
    const initial = setTimeout(check, 0);
    const interval = setInterval(check, 10000);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(interval); };
  }, [updatedAt]);

  // ── Source attribution centerpiece ───────────────────────────────
  // The empty gray thumbnail previously read as a "loading bug" -
  // grounding the wait in the actual source (favicon + domain) tells
  // the user the pipeline is real and knows what it's working on.
  // Google's S2 service is cached server-side, falls back to a generic
  // globe glyph if the host is unknown.
  const host = useMemo(() => _hostFromUrl(url), [url]);
  const [faviconOk, setFaviconOk] = useState(true);
  const faviconSrc = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=128` : null;
  const StageIcon = stageData.icon || Globe;

  return (
    <div className="aspect-video relative overflow-hidden bg-zinc-100" data-testid="processing-overlay">
      {/* Skeleton fade-in (Variant F): the card progressively assembles
          itself. We layer (1) a soft emerald pulse glow, (2) the source
          favicon centerpiece, and (3) a stage pill so the user always
          has three honest signals: WHAT we're processing, WHERE in the
          pipeline we are, and visual proof we're still alive. */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-50 via-white to-zinc-100" />

      {/* Animated brand aura behind the favicon - slow, calm, breathes
          at the same cadence as the spinner so it feels intentional. */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-emerald-300/30 blur-2xl animate-skel-pulse"
        aria-hidden
      />

      {/* Centerpiece - favicon + domain, with the stage pill stacked
          directly below so the eye lands on a single vertical column.
          The previous top-left pill competed for attention with the
          centerpiece; this is calmer and the stage label feels like a
          subtitle to the source instead of a floating badge. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
        <div className="relative w-14 h-14 rounded-2xl bg-white shadow-md ring-1 ring-zinc-200 flex items-center justify-center overflow-hidden">
          {faviconSrc && faviconOk ? (
            <img
              src={faviconSrc}
              alt=""
              className="w-10 h-10 object-contain"
              onError={() => setFaviconOk(false)}
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <StageIcon className="w-7 h-7 text-emerald-500" strokeWidth={1.8} />
          )}
        </div>
        {host ? (
          <div className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 max-w-[70%] truncate">
            <Link2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{host}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[11px] font-medium text-zinc-400">
            <Brain className="w-3 h-3 shrink-0" />
            <span>BrainSync is thinking…</span>
          </div>
        )}
        {/* Stage chip - same emerald pill the rest of the app uses,
            now sitting directly under the host so the column reads as
            one unit (favicon → domain → stage). */}
        <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
          <Loader2 className="w-2.5 h-2.5 animate-spin" />
          <span>{stageData.label}...</span>
        </span>
      </div>

      {isStuck && onRetry && (
        <button
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className="absolute bottom-2 right-2 z-10 text-[10px] font-medium text-primary bg-white/95 px-2 py-0.5 rounded shadow-sm hover:bg-white transition-colors"
          data-testid="stuck-retry-btn"
        >
          Tap to retry
        </button>
      )}

      <style>{`
        @keyframes skel-shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } }
        .animate-skel-shimmer { background-size: 200% 100%; animation: skel-shimmer 1.5s linear infinite }
        @keyframes skel-pulse { 0%, 100% { opacity: 0.35; transform: translate(-50%, -50%) scale(0.85); } 50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.05); } }
        .animate-skel-pulse { animation: skel-pulse 2.4s ease-in-out infinite }
      `}</style>
    </div>
  );
}

export function ProcessingBlockPlaceholder({ stage, label }) {
  const stageData = STAGES.find(s => s.key === stage) || STAGES[0];

  return (
    <div
      className="rounded-2xl bg-zinc-50 border border-zinc-200 p-5 space-y-3"
      data-testid="processing-placeholder"
    >
      {/* Skeleton fade-in for the BookmarkDetail summary/transcript
          slot - same visual language as the card overlay. The slot
          fills with shimmer lines until the backend hands us the real
          content; the stage pill + ProcessingSteps row give the user
          honest progress signals. */}
      <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>{label || `${stageData.label}...`}</span>
      </div>

      <div className="space-y-2">
        <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-skel-shimmer" />
        <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-skel-shimmer" style={{ width: '92%' }} />
        <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-skel-shimmer" style={{ width: '78%' }} />
        <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-skel-shimmer" style={{ width: '85%' }} />
        <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-skel-shimmer" style={{ width: '70%' }} />
      </div>

      <ProcessingSteps stage={stage} />

      <style>{`
        @keyframes skel-shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } }
        .animate-skel-shimmer { background-size: 200% 100%; animation: skel-shimmer 1.5s linear infinite }
      `}</style>
    </div>
  );
}
